import {
  Button,
  cn,
  Combobox,
  type ComboboxOption,
  DateTimePicker,
  FormField,
  FormFieldError,
  FormFieldHint,
  FormFieldLabel,
  Input,
  Progress,
  RadioCardGroup,
  RadioCardItem,
  RichTextEditor,
} from '@eureca/ui';
import { ConsultantsMenu } from '@/shared/consultants-menu/ConsultantsMenu';
import { APP_TIMEZONE } from '@/lib/timezone';
import { ArchiveIcon, UploadIcon, XIcon } from './icons';

// Campo de capa EXIBIDO porém DESABILITADO (EU-240) — o upload de capa não está
// liberado neste momento, então o campo aparece em modo read-only nos dois modos
// (create/edit) para sinalizar que existe mas não é editável ainda.
const SHOW_COVER_FIELD = true;

export interface ProgramCreateValues {
  companyId: string;
  /** Display label for the picked company. Not validated — derived from the selected combobox option. */
  companyName: string;
  name: string;
  description: string;
  contractType: string;
  startDate: string;
  endDate: string;
  targetRegistrations: string;
}

export interface ProgramCreatePageProps {
  values: ProgramCreateValues;
  errors: Record<string, string>;
  coverFileName: string | null;
  /** Object URL for the currently-picked cover. Null when no cover is set. */
  coverPreviewUrl: string | null;
  isSubmitting: boolean;
  progress: number;
  /** Async company search results — empty until the user types ≥ 2 chars. */
  companyOptions: ComboboxOption[];
  /** Tipos do catálogo (RN-01) — já ordenados pelo rótulo. */
  contractTypeOptions: readonly ProgramContractTypeOption[];
  isCompaniesLoading: boolean;
  onCompanySearch: (query: string) => void;
  onCompanyChange: (id: string, name: string) => void;
  onFieldChange: (
    field: Exclude<keyof ProgramCreateValues, 'companyId' | 'companyName'>,
    value: string
  ) => void;
  onCoverChange: (file: File | null) => void;
  onSubmit: () => void;
}

/**
 * EU-709 — a lista dos tipos deixou de viver aqui.
 *
 * Eram quatro valores hardcoded, duplicados entre esta tela e a de edição de
 * programa, e nada os amarrava ao `Tipo de vaga`. A RN-01 exige que as duas
 * listas contenham SEMPRE os mesmos valores, então elas passaram a ler de uma
 * origem única: `GET /catalogues/contract-types`. As opções chegam por prop,
 * já ordenadas pelo rótulo, e o `value` continua sendo a
 * `core.contract_type.key` que o upsert do Core resolve.
 */
export interface ProgramContractTypeOption {
  value: string;
  label: string;
}

export function ProgramCreatePage({
  values,
  errors,
  coverFileName,
  coverPreviewUrl,
  isSubmitting,
  progress,
  companyOptions,
  contractTypeOptions,
  isCompaniesLoading,
  onCompanySearch,
  onCompanyChange,
  onFieldChange,
  onCoverChange,
  onSubmit,
}: ProgramCreatePageProps) {
  return (
    <div className="flex min-h-dvh flex-row bg-muted">
      <ConsultantsMenu activeId="programs" />

      <main className="flex flex-1 flex-col items-center gap-8 bg-card px-4 pt-6 pb-16 md:px-8 md:pt-8">
        <div className="flex w-full max-w-[630px] flex-col gap-8">
          <div className="flex flex-col gap-2">
            <div className="flex flex-row items-center gap-2">
              <div className="flex size-8 items-center justify-center rounded-md bg-secondary text-secondary-foreground">
                <ArchiveIcon className="size-4" />
              </div>
              <h1 className="text-3xl font-medium leading-none tracking-tight text-foreground md:text-4xl">
                Criação de programa
              </h1>
            </div>
            <p className="text-xl font-medium leading-none tracking-tight text-accent-foreground md:text-2xl">
              Informações gerais
            </p>
          </div>

          <Progress value={progress} className="h-2 bg-secondary/40" />

          <form
            className="flex flex-col gap-5"
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit();
            }}
            noValidate
          >
            <FormField error={Boolean(errors.companyId)}>
              <FormFieldLabel className="text-foreground">Empresa</FormFieldLabel>
              <Combobox
                options={companyOptions}
                value={values.companyId || undefined}
                onSelect={(id) => {
                  const opt = companyOptions.find((o) => o.value === id);
                  onCompanyChange(id, opt?.label ?? values.companyName);
                }}
                onSearchChange={onCompanySearch}
                placeholder={values.companyName || 'Selecione a empresa'}
                searchPlaceholder="Buscar por nome…"
                emptyMessage="Nenhuma empresa encontrada"
                loading={isCompaniesLoading}
                error={Boolean(errors.companyId)}
                data-testid="program-create-company"
              />
              <FormFieldError>{errors.companyId}</FormFieldError>
            </FormField>

            <FormField error={Boolean(errors.name)}>
              <FormFieldLabel className="text-foreground">Nome do programa</FormFieldLabel>
              <Input
                value={values.name}
                placeholder="Ex.: Programa Trilhas do Futuro"
                variant={errors.name ? 'destructive' : 'default'}
                onChange={(event) => onFieldChange('name', event.target.value)}
                data-testid="program-create-name"
              />
              <FormFieldError>{errors.name}</FormFieldError>
            </FormField>

            <FormField error={Boolean(errors.description)}>
              <FormFieldLabel className="text-foreground">Descrição</FormFieldLabel>
              <RichTextEditor
                value={values.description}
                hasError={Boolean(errors.description)}
                onChange={(html) => onFieldChange('description', html)}
                data-testid="program-create-description"
              />
              <FormFieldError>{errors.description}</FormFieldError>
            </FormField>

            <FormField error={Boolean(errors.contractType)}>
              {/* EU-709 RN-01 — o campo é `Tipo de programa`, espelhado com o
                  `Tipo de vaga`. O rótulo antigo ("Tipo de contratação") era o
                  nome interno da tabela de referência, não o do produto. */}
              <FormFieldLabel className="text-foreground">Tipo de programa</FormFieldLabel>
              <RadioCardGroup
                value={values.contractType}
                onValueChange={(value) => onFieldChange('contractType', value)}
              >
                {contractTypeOptions.map((option) => (
                  <RadioCardItem
                    key={option.value}
                    value={option.value}
                    className="group px-4 py-4"
                    data-testid={`program-create-contract-${option.value}`}
                  >
                    {option.label}
                  </RadioCardItem>
                ))}
              </RadioCardGroup>
              <FormFieldError>{errors.contractType}</FormFieldError>
            </FormField>

            <div className="flex flex-col gap-5 sm:flex-row">
              <FormField error={Boolean(errors.startDate)} className="flex-1">
                <FormFieldLabel className="text-foreground">Início das inscrições</FormFieldLabel>
                <DateTimePicker
                  value={values.startDate}
                  timeZone={APP_TIMEZONE}
                  hasError={Boolean(errors.startDate)}
                  onChange={(value) => onFieldChange('startDate', value)}
                  data-testid="program-create-start-date"
                />
                <FormFieldError>{errors.startDate}</FormFieldError>
              </FormField>

              <FormField error={Boolean(errors.endDate)} className="flex-1">
                <FormFieldLabel className="text-foreground">Fim das inscrições</FormFieldLabel>
                <DateTimePicker
                  value={values.endDate}
                  timeZone={APP_TIMEZONE}
                  defaultTime="23:59"
                  hasError={Boolean(errors.endDate)}
                  onChange={(value) => onFieldChange('endDate', value)}
                  data-testid="program-create-end-date"
                />
                <FormFieldError>{errors.endDate}</FormFieldError>
              </FormField>
            </div>

            <FormField error={Boolean(errors.targetRegistrations)}>
              <FormFieldLabel className="text-foreground">Meta de inscritos</FormFieldLabel>
              <Input
                value={values.targetRegistrations}
                placeholder="Ex.: 500"
                inputMode="numeric"
                pattern="[0-9]*"
                variant={errors.targetRegistrations ? 'destructive' : 'default'}
                data-testid="program-create-target-registrations"
                // Strip every non-digit before propagating — paste, autofill,
                // or any keystroke that produces letters/symbols never reaches
                // the form value. Zod's `\d+` regex is the safety net.
                onChange={(event) =>
                  onFieldChange('targetRegistrations', event.target.value.replace(/\D/g, ''))
                }
              />
              <FormFieldError>{errors.targetRegistrations}</FormFieldError>
            </FormField>

            {/* Seção "Capa" EXIBIDA porém DESABILITADA (EU-240). */}
            {SHOW_COVER_FIELD ? (
              <FormField error={Boolean(errors.cover)}>
                <FormFieldLabel className="text-foreground">Capa</FormFieldLabel>
                <CoverFileInput
                  fileName={coverFileName}
                  previewUrl={coverPreviewUrl}
                  hasError={Boolean(errors.cover)}
                  onChange={onCoverChange}
                  disabled
                />
                <FormFieldHint>
                  Envie em .JPG ou .PNG, com no máximo 5 MB e com aspect ratio de 16 : 9.
                </FormFieldHint>
                <FormFieldError>{errors.cover}</FormFieldError>
              </FormField>
            ) : null}

            <div className="flex flex-row justify-end pt-3">
              <Button
                type="submit"
                size="lg"
                loading={isSubmitting}
                data-testid="program-create-submit"
              >
                Avançar
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

interface CoverFileInputProps {
  fileName: string | null;
  /** When set, renders a 16:9 preview thumbnail above the picker with an X-on-hover. */
  previewUrl: string | null;
  hasError: boolean;
  onChange: (file: File | null) => void;
  /** Read-only mode — picker is greyed/non-interactive and the remove button is hidden. */
  disabled?: boolean;
}

function CoverFileInput({
  fileName,
  previewUrl,
  hasError,
  onChange,
  disabled = false,
}: CoverFileInputProps) {
  const label = hasError
    ? 'Selecione outro arquivo'
    : (fileName ?? 'Clique para selecionar um arquivo');
  return (
    <div className="flex flex-col gap-2">
      {previewUrl && (
        <div
          className={cn(
            'group relative w-full overflow-hidden rounded-md border bg-muted',
            // 16:9 — matches the rest of the validation surface.
            'aspect-video',
            hasError ? 'border-destructive' : 'border-input'
          )}
        >
          <img
            src={previewUrl}
            alt="Pré-visualização da capa do programa"
            className="size-full object-cover"
            data-testid="program-create-cover-preview"
          />
          {!disabled && (
            <button
              type="button"
              // Visible on hover OR keyboard-focus so it's still reachable for AT.
              className={cn(
                'absolute top-2 right-2 flex size-7 items-center justify-center rounded-full',
                'bg-background/90 text-foreground shadow-2xs ring-1 ring-input',
                'opacity-0 transition-opacity duration-150',
                'group-hover:opacity-100 focus-visible:opacity-100',
                'hover:bg-destructive hover:text-destructive-foreground hover:ring-destructive'
              )}
              aria-label="Remover capa"
              onClick={() => onChange(null)}
              data-testid="program-create-cover-remove"
            >
              <XIcon className="size-4" aria-hidden />
            </button>
          )}
        </div>
      )}
      <label
        aria-disabled={disabled}
        className={cn(
          'flex h-9 w-full flex-row items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm shadow-2xs transition-colors',
          disabled
            ? 'cursor-not-allowed opacity-60'
            : 'cursor-pointer focus-within:ring-2 hover:bg-muted/30',
          hasError
            ? 'border-destructive focus-within:ring-destructive/40'
            : 'border-input focus-within:ring-primary/40'
        )}
      >
        <UploadIcon className="size-4 text-foreground" />
        <span className="flex-1 truncate font-medium text-foreground">{label}</span>
        <input
          type="file"
          accept="image/jpeg,image/png"
          className="sr-only"
          data-testid="program-create-cover"
          disabled={disabled}
          onChange={(event) => {
            const file = event.target.files?.[0] ?? null;
            onChange(file);
          }}
        />
      </label>
    </div>
  );
}
